import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { authFetch } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  DollarSign, 
  Zap, 
  TrendingUp, 
  Calendar,
  Clock,
  User,
  BarChart3,
  Loader2,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

export const BillingPage = () => {
  console.log('[BillingPage] Component mounting...');
  const { user } = useAuth();
  const { isBarbiePink } = useTheme();
  console.log('[BillingPage] Hooks loaded, user:', user?.email);
  const [loading, setLoading] = useState(true);
  const [usageData, setUsageData] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [periodDays, setPeriodDays] = useState('30');
  const [error, setError] = useState(null);

  const fetchUsageData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [usageRes, logsRes] = await Promise.all([
        authFetch(`${API}/billing/usage?days=${periodDays}`),
        authFetch(`${API}/billing/recent-logs?limit=50`)
      ]);

      if (usageRes.ok) {
        const data = await usageRes.json();
        setUsageData(data);
      } else if (usageRes.status === 403) {
        setError('Admin access required');
        toast.error('Admin access required');
      } else {
        setError(`Failed to load data: ${usageRes.status}`);
      }

      if (logsRes.ok) {
        setRecentLogs(await logsRes.json());
      }
    } catch (err) {
      console.error('Failed to fetch billing data:', err);
      setError(err.message || 'Failed to load billing data');
      toast.error('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  }, [periodDays]);

  useEffect(() => {
    fetchUsageData();
  }, [fetchUsageData]);

  const formatCost = (cost) => {
    if (cost === undefined || cost === null) return '$0.00';
    if (cost < 0.01) {
      return `$${cost.toFixed(6)}`;
    }
    return `$${cost.toFixed(4)}`;
  };

  const formatNumber = (num) => {
    if (num === undefined || num === null) return '0';
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(2)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  if (loading) {
    console.log('[BillingPage] Showing loading state');
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !usageData) {
    console.log('[BillingPage] Showing error state:', error);
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <AlertCircle className="w-12 h-12 text-yellow-500" />
        <p className="text-muted-foreground">{error || 'Unable to load billing data. Admin access required.'}</p>
        <Button variant="outline" onClick={fetchUsageData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  console.log('[BillingPage] Showing main content');

  // Safely destructure with defaults to prevent crashes
  const { 
    summary = {}, 
    daily = [], 
    weekly = [], 
    monthly = [], 
    by_user = [], 
    cost_rates = { input_per_million: 0, output_per_million: 0, model: 'unknown' } 
  } = usageData || {};

  return (
    <div data-testid="billing-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">AI Usage & Billing</h1>
          <p className="text-muted-foreground mt-1">
            Track AI credit usage and costs
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={periodDays} onValueChange={setPeriodDays}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={fetchUsageData}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className={cn(isBarbiePink && "card-brutalist")}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
            <DollarSign className="w-4 h-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatCost(summary.total_cost || 0)}</div>
            <p className="text-xs text-muted-foreground">Last {summary.period_days || periodDays} days</p>
          </CardContent>
        </Card>

        <Card className={cn(isBarbiePink && "card-brutalist")}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Requests</CardTitle>
            <Zap className="w-4 h-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(summary.total_requests || 0)}</div>
            <p className="text-xs text-muted-foreground">AI polish calls</p>
          </CardContent>
        </Card>

        <Card className={cn(isBarbiePink && "card-brutalist")}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Tokens</CardTitle>
            <BarChart3 className="w-4 h-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(summary.total_tokens || 0)}</div>
            <p className="text-xs text-muted-foreground">
              {formatNumber(summary.total_input_tokens || 0)} in / {formatNumber(summary.total_output_tokens || 0)} out
            </p>
          </CardContent>
        </Card>

        <Card className={cn(isBarbiePink && "card-brutalist")}>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Cost Rates</CardTitle>
            <TrendingUp className="w-4 h-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-sm">
              <p>Input: ${cost_rates.input_per_million}/1M</p>
              <p>Output: ${cost_rates.output_per_million}/1M</p>
            </div>
            <Badge variant="outline" className="mt-1">{cost_rates.model}</Badge>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">
            <Calendar className="w-4 h-4 mr-2" />
            Daily
          </TabsTrigger>
          <TabsTrigger value="weekly" data-testid="tab-weekly">
            <BarChart3 className="w-4 h-4 mr-2" />
            Weekly
          </TabsTrigger>
          <TabsTrigger value="monthly" data-testid="tab-monthly">
            <TrendingUp className="w-4 h-4 mr-2" />
            Monthly
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">
            <User className="w-4 h-4 mr-2" />
            By User
          </TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">
            <Clock className="w-4 h-4 mr-2" />
            Recent Logs
          </TabsTrigger>
        </TabsList>

        {/* Daily View */}
        <TabsContent value="overview" className="mt-6">
          <Card className={cn(isBarbiePink && "card-brutalist")}>
            <CardHeader>
              <CardTitle>Daily Usage</CardTitle>
              <CardDescription>Day-by-day breakdown of AI usage and costs</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Requests</TableHead>
                    <TableHead className="text-right">Input Tokens</TableHead>
                    <TableHead className="text-right">Output Tokens</TableHead>
                    <TableHead className="text-right">Total Tokens</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {daily.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No usage data for this period
                      </TableCell>
                    </TableRow>
                  ) : (
                    daily.map((day) => (
                      <TableRow key={day.date}>
                        <TableCell className="font-medium">{day.date}</TableCell>
                        <TableCell className="text-right">{day.requests}</TableCell>
                        <TableCell className="text-right">{formatNumber(day.input_tokens)}</TableCell>
                        <TableCell className="text-right">{formatNumber(day.output_tokens)}</TableCell>
                        <TableCell className="text-right">{formatNumber(day.total_tokens)}</TableCell>
                        <TableCell className="text-right font-medium text-green-600">
                          {formatCost(day.cost)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Weekly View */}
        <TabsContent value="weekly" className="mt-6">
          <Card className={cn(isBarbiePink && "card-brutalist")}>
            <CardHeader>
              <CardTitle>Weekly Usage</CardTitle>
              <CardDescription>Week-by-week summary of AI usage</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Week Starting</TableHead>
                    <TableHead className="text-right">Requests</TableHead>
                    <TableHead className="text-right">Total Tokens</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {weekly.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground">
                        No usage data for this period
                      </TableCell>
                    </TableRow>
                  ) : (
                    weekly.map((week) => (
                      <TableRow key={week.week_start}>
                        <TableCell className="font-medium">{week.week_start}</TableCell>
                        <TableCell className="text-right">{week.requests}</TableCell>
                        <TableCell className="text-right">{formatNumber(week.total_tokens)}</TableCell>
                        <TableCell className="text-right font-medium text-green-600">
                          {formatCost(week.cost)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Monthly View */}
        <TabsContent value="monthly" className="mt-6">
          <Card className={cn(isBarbiePink && "card-brutalist")}>
            <CardHeader>
              <CardTitle>Monthly Usage</CardTitle>
              <CardDescription>Month-by-month summary of AI usage</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Requests</TableHead>
                    <TableHead className="text-right">Total Tokens</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monthly.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground">
                        No usage data for this period
                      </TableCell>
                    </TableRow>
                  ) : (
                    monthly.map((month) => (
                      <TableRow key={month.month}>
                        <TableCell className="font-medium">{month.month}</TableCell>
                        <TableCell className="text-right">{month.requests}</TableCell>
                        <TableCell className="text-right">{formatNumber(month.total_tokens)}</TableCell>
                        <TableCell className="text-right font-medium text-green-600">
                          {formatCost(month.cost)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* By User View */}
        <TabsContent value="users" className="mt-6">
          <Card className={cn(isBarbiePink && "card-brutalist")}>
            <CardHeader>
              <CardTitle>Usage by User</CardTitle>
              <CardDescription>AI usage breakdown by team member</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="text-right">Requests</TableHead>
                    <TableHead className="text-right">Total Tokens</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {by_user.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground">
                        No usage data for this period
                      </TableCell>
                    </TableRow>
                  ) : (
                    by_user.map((userStat) => (
                      <TableRow key={userStat.email}>
                        <TableCell className="font-medium">{userStat.email}</TableCell>
                        <TableCell className="text-right">{userStat.requests}</TableCell>
                        <TableCell className="text-right">{formatNumber(userStat.total_tokens)}</TableCell>
                        <TableCell className="text-right font-medium text-green-600">
                          {formatCost(userStat.cost)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recent Logs View */}
        <TabsContent value="logs" className="mt-6">
          <Card className={cn(isBarbiePink && "card-brutalist")}>
            <CardHeader>
              <CardTitle>Recent AI Requests</CardTitle>
              <CardDescription>Last 50 AI usage logs</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Feature</TableHead>
                    <TableHead className="text-right">Tokens</TableHead>
                    <TableHead className="text-right">Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground">
                        No recent AI requests
                      </TableCell>
                    </TableRow>
                  ) : (
                    recentLogs.map((log) => (
                      <TableRow key={log.log_id}>
                        <TableCell className="text-xs">
                          {new Date(log.created_at).toLocaleString('en-AU', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                            timeZone: 'Australia/Sydney'
                          })}
                        </TableCell>
                        <TableCell className="text-xs">{log.user_email?.split('@')[0]}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {log.feature}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-xs">{formatNumber(log.total_tokens)}</TableCell>
                        <TableCell className="text-right text-xs text-green-600">
                          {formatCost(log.total_cost)}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BillingPage;
